import type { Tour } from "./types";

/**
 * Operate tours — one per primary page. Each tour articulates the page's
 * value prop in 4-6 steps. Stable id includes a v1 suffix; bump (v1 → v2)
 * when content changes meaningfully so existing users see the new tour.
 *
 * Naming: `operate.<page>.v<n>`. localStorage gates dismissal per id.
 */

const NAV = (path: string) => `nav[data-tour-id="nav"] a[href="${path}"]`;

export const chatTour: Tour = {
  id: "operate.chat.v1",
  title: "Welcome to Operate",
  subtitle: "Your Cognos audit data, asked in plain English.",
  steps: [
    {
      placement: "center",
      title: "Operate turns Cognos audit data into a conversation",
      body:
        "Ask any question about who's running what, when, where, and how — in plain English. The agent picks the right metadata tools, runs the queries, and explains the answer with full lineage.",
    },
    {
      target: '[data-tour-id="chat-input"]',
      placement: "top",
      title: "Ask anything",
      body:
        "Try: \"Who ran the most reports last month?\" or \"Show me failed report executions in the last 7 days, grouped by owner.\" Operate handles ambiguity — re-ask in plain English; no SQL required.",
    },
    {
      target: '[data-tour-id="grounded-badge"]',
      placement: "bottom",
      title: "Grounded answers",
      body:
        "Every answer cites the specific Cognos audit tables and joins it used. The Grounded badge in the header confirms the semantic layer that's keeping the model honest is active.",
    },
    {
      target: NAV("/saved"),
      placement: "bottom",
      title: "Save what works",
      body:
        "Found a question that's worth re-running? Save it from any chat and it shows up under Saved — your team's growing playbook of high-value queries.",
    },
    {
      target: NAV("/digests"),
      placement: "bottom",
      title: "Schedule + Rules",
      body:
        "Promote saved questions into recurring email digests and threshold alerts. Operate goes from a chat surface to an automated reporting pipeline without ever leaving the agent.",
    },
    {
      placement: "center",
      title: "Replay this tour anytime",
      body:
        "Click the help icon (?) in the header to replay this tour or any other page's tour. Press Esc to dismiss; arrow keys + Enter to navigate.",
    },
  ],
};

export const savedTour: Tour = {
  id: "operate.saved.v1",
  title: "Saved questions",
  subtitle: "A curated playbook of high-value Cognos queries.",
  steps: [
    {
      placement: "center",
      title: "Your team's BI playbook",
      body:
        "Saved questions are the Cognos questions worth running again — Friday recap queries, monthly KPI rollups, anomaly checks. They become the seed for digests and rules.",
    },
    {
      target: '[data-tour-id="saved-list"]',
      placement: "top",
      title: "Run, edit, or schedule",
      body:
        "Each saved question has a Run button (replays the agent flow) and a Schedule button (promotes it to a digest). Edit the title / description to make it self-explanatory for teammates.",
    },
    {
      target: NAV("/digests"),
      placement: "bottom",
      title: "Promote to a digest",
      body:
        "Anything saved here can be scheduled as a recurring digest — daily, weekly, or threshold-triggered. The result lands in inboxes, no extra wiring.",
    },
  ],
};

export const digestsTour: Tour = {
  id: "operate.digests.v1",
  title: "Digests",
  subtitle: "Schedule recurring Cognos answers to your inbox.",
  steps: [
    {
      placement: "center",
      title: "Recurring intelligence",
      body:
        "Digests turn one-shot questions into a Monday-morning ritual. The agent re-runs the saved question on schedule, generates the visualization, and emails the result.",
    },
    {
      target: '[data-tour-id="digests-create"]',
      placement: "left",
      title: "Create a digest",
      body:
        "Pick a saved question, set the cadence (daily / weekly / monthly), and choose recipients. The first run goes out at the next slot — preview is always one click away.",
    },
    {
      target: '[data-tour-id="digests-list"]',
      placement: "top",
      title: "Pause, preview, edit",
      body:
        "Each digest can be paused without deleting, previewed inline, or edited. The last-run status tells you at a glance whether yesterday's send actually landed.",
    },
  ],
};

export const rulesTour: Tour = {
  id: "operate.rules.v1",
  title: "Rules",
  subtitle: "Threshold-triggered alerts on Cognos health.",
  steps: [
    {
      placement: "center",
      title: "Watchful intelligence",
      body:
        "Rules fire when a saved question's result crosses a threshold — \"failed runs > 50\", \"top user logged in 0 times in 7 days\", \"cube build success rate < 95%\". Quiet by default, loud only when something matters.",
    },
    {
      target: '[data-tour-id="rules-create"]',
      placement: "left",
      title: "Build a rule",
      body:
        "Pick a saved question, define the threshold, choose a notification channel. Operate evaluates the rule on schedule and only alerts when the threshold is crossed.",
    },
    {
      target: '[data-tour-id="rules-list"]',
      placement: "top",
      title: "Calm, audited, replayable",
      body:
        "Every rule keeps a log of evaluations and triggers. Tune thresholds with a click; the next evaluation respects the change immediately.",
    },
  ],
};

export const historyTour: Tour = {
  id: "operate.history.v1",
  title: "History",
  subtitle: "Every chat, every tool call, every answer.",
  steps: [
    {
      placement: "center",
      title: "Operate remembers",
      body:
        "Every conversation is preserved with the exact tool calls, SQL, and answers. Resume an old thread to keep questioning, or use a past answer as the seed for a new one.",
    },
    {
      target: '[data-tour-id="history-list"]',
      placement: "right",
      title: "Resume a conversation",
      body:
        "Click any conversation to replay the full transcript with rendered tool outputs. The agent can pick up where you left off without losing context.",
    },
    {
      target: NAV("/audit"),
      placement: "bottom",
      title: "Audit trail",
      body:
        "For compliance teams: every Operate call lands in the Audit log with caller, prompt, tools, and outputs. No hidden actions.",
    },
  ],
};

export const auditTour: Tour = {
  id: "operate.audit.v1",
  title: "AI audit log",
  subtitle: "Compliance-grade record of every agent action.",
  steps: [
    {
      placement: "center",
      title: "Trust by audit",
      body:
        "Every chat, every MCP tool call, every answer is logged with caller, timestamp, prompt text, tool inputs, and outputs. SOC 2 / HIPAA / internal-audit teams have a single source of truth.",
    },
    {
      target: '[data-tour-id="audit-table"]',
      placement: "top",
      title: "Filter, search, export",
      body:
        "Filter by user, tool, date range, or success/failure. Export the result to CSV for compliance reviews. Retention is governed by your organization's policy.",
    },
    {
      placement: "center",
      title: "Restricted by role",
      body:
        "The Audit page is gated to ADMIN / SUPER_ADMIN — the same roles that approve write tools (cube-ops, propagation). Operators see their own history; auditors see everyone's.",
    },
  ],
};

export const settingsTour: Tour = {
  id: "operate.settings.v1",
  title: "Settings",
  subtitle: "Tune Operate without restarting the bundle.",
  steps: [
    {
      placement: "center",
      title: "Live configuration",
      body:
        "Most knobs are environment-bound (set in conf/.env at boot), but the read-only Settings view shows you exactly what Operate believes about its LLM, semantic layer, lens sync state, and feature flags.",
    },
    {
      target: '[data-tour-id="settings-llm"]',
      placement: "right",
      title: "LLM provider",
      body:
        "Confirm which provider is active and which model is bound. Switching providers requires editing conf/.env and restarting; deterministic mode toggles per request.",
    },
    {
      target: '[data-tour-id="settings-semantic"]',
      placement: "right",
      title: "Semantic layer + lens",
      body:
        "The semantic profile controls which audit tables Operate exposes to the LLM. Lens (when enabled) mirrors Cognos's content store + audit DB into Postgres for richer cross-table joins.",
    },
  ],
};

export const recycleTour: Tour = {
  id: "operate.recycle.v1",
  title: "Recycle bin",
  subtitle: "Find and recover deleted Cognos content.",
  steps: [
    {
      placement: "center",
      title: "Pick + restore deleted content",
      body:
        "Cognos's recycle bin holds soft-deleted content for a retention window — typically 30 days. This page surfaces every deleted item the lens replica has captured and lets admins restore them in one click (or in bulk).",
    },
    {
      target: '[data-tour-id="recycle-filters"]',
      placement: "bottom",
      title: "Filter by what matters",
      body:
        "Search by name / path / user; restrict to a folder scope; pick a deletion window; filter by who deleted. The recoverability dropdown lets you peek past-retention items even when the Settings default hides them.",
    },
    {
      target: '[data-tour-id="recycle-table"]',
      placement: "top",
      title: "Inspect, restore, hide",
      body:
        "Click any name to open the Details drawer — pre-flight blockers + a single-item restore form with optional rename. The trash icon hides a row from your view (UI-only filter, never deletes anything).",
    },
    {
      placement: "center",
      title: "Bulk restore",
      body:
        "Select multiple rows with the checkboxes — a bulk-action bar appears at the bottom. Click 'Restore N' to open the per-item rename modal; submit returns a per-item OK/failure receipt. The agent can do the same flow via cognos.recycle.restoreBulk.",
    },
  ],
};

/**
 * Cross-page guided flow — walks a new user through the canonical
 * Operate value loop: ask a question → save it → schedule it as a
 * digest. Unlike the page tours, this one carries `path` on its
 * steps, so the overlay navigates the router as the user advances.
 *
 * Demonstrates every enhanced-step capability:
 *  - `path`         — router navigation between steps
 *  - `waitForEvent` — the "ask a question" step gates Next until the
 *                     user actually submits a chat (ChatPage dispatches
 *                     `tour:chat-submitted` on send)
 *  - `docHref`      — inline "Learn more" link to the prompt library
 *
 * `crossPage: true` keeps it OUT of the page-mount auto-start path —
 * it's launched only from the onboarding hub / launcher.
 */
export const firstValueFlow: Tour = {
  id: "operate.flow.first-value.v1",
  title: "Your first answer → digest",
  subtitle: "The end-to-end Operate loop in 5 steps.",
  crossPage: true,
  category: "flow",
  estMinutes: 4,
  steps: [
    {
      path: "/chat",
      placement: "center",
      title: "The Operate loop, start to finish",
      body:
        "This flow walks you across three pages — Chat, Saved, Digests — to show how a one-off question becomes an automated recurring report. It navigates for you; just follow along.",
    },
    {
      path: "/chat",
      target: '[data-tour-id="chat-input"]',
      placement: "top",
      title: "Ask a real question",
      body:
        "Type something like \"Show me the top 10 report runners last month\" and submit it. The tour waits here until you've actually asked — go ahead, try it.",
      waitForEvent: "tour:chat-submitted",
      waitHint: "Submit a question to continue",
      docHref: "/saved",
      docLabel: "Browse the prompt library for ideas",
    },
    {
      path: "/saved",
      target: '[data-tour-id="saved-list"]',
      placement: "top",
      title: "Save the ones worth keeping",
      body:
        "Good questions get saved here — your team's reusable playbook. The Save button on any chat answer pins it; the prompt library seeds dozens more.",
    },
    {
      path: "/digests",
      target: '[data-tour-id="digests-create"]',
      placement: "left",
      title: "Schedule it as a digest",
      body:
        "Pick a saved question, set a cadence, choose recipients. Now the answer arrives on a schedule — Operate just turned a question into an automated reporting pipeline.",
    },
    {
      path: "/digests",
      placement: "center",
      title: "That's the whole loop",
      body:
        "Ask → Save → Schedule. Everything else in Operate — Rules, Audit, Recycle bin — hangs off this core. Replay any page's tour from the help (?) menu in the header.",
    },
  ],
};

/**
 * Full catalog of every tour, for the onboarding hub. The page tours
 * are also reachable via {@link tourForPath} (auto-start on page
 * mount); cross-page flows live only here. Order is the hub's
 * display order.
 */
export const TOUR_CATALOG: Tour[] = [
  firstValueFlow,
  chatTour,
  savedTour,
  digestsTour,
  rulesTour,
  historyTour,
  auditTour,
  recycleTour,
  settingsTour,
];

/**
 * Lookup the right tour for the current React Router pathname. Returns
 * undefined when no tour is registered for the path (e.g. /login). Pages
 * call this rather than importing a specific tour, so the routing logic
 * stays in one place. Cross-page flows are intentionally excluded —
 * they aren't page-scoped.
 */
export function tourForPath(pathname: string): Tour | undefined {
  if (pathname.startsWith("/chat")) return chatTour;
  if (pathname.startsWith("/saved")) return savedTour;
  if (pathname.startsWith("/digests")) return digestsTour;
  if (pathname.startsWith("/rules")) return rulesTour;
  if (pathname.startsWith("/history")) return historyTour;
  if (pathname.startsWith("/audit")) return auditTour;
  if (pathname.startsWith("/recycle")) return recycleTour;
  if (pathname.startsWith("/settings")) return settingsTour;
  return undefined;
}
